import { PropsWithChildren, useEffect, useState } from 'react'
import { ImageSource, RasterSourceSpecification } from 'mapbox-gl'

import { MapboxCoordinates } from '@/domain/protocols/Coordinates'

import { useMapContext } from './MapContext'
import { hasLiveStyle, safeAddSource, safeRemoveSource } from './mapTeardown'

interface MapRasterSourceProps {
  id: string
  tiles?: string[]
  url?: string
  tileSize?: number
  // Mapbox raster `bounds` as [west, south, east, north]. When set, Mapbox only
  // requests tiles inside this box, so out-of-coverage tiles are never fetched.
  bounds?: [number, number, number, number]
  // Source-level zoom range. Caps which zoom levels Mapbox requests tiles for
  // (and enables overzoom of the maxZoom tiles), avoiding requests for zoom
  // levels the tile set does not contain.
  minZoom?: number
  maxZoom?: number
  coordinates?: MapboxCoordinates
}

function MapRasterSource(props: PropsWithChildren<MapRasterSourceProps>) {
  const { id, tiles, url, tileSize = 256, bounds, minZoom, maxZoom, coordinates } = props

  const [sourceLoaded, setSourceLoaded] = useState(false)
  const { map } = useMapContext()

  // Add source logic
  useEffect(() => {
    if (!map) return
    if (!tiles && !url) return

    if (tiles) {
      const source: RasterSourceSpecification = {
        type: 'raster',
        tiles,
        tileSize
      }
      if (bounds) source.bounds = bounds
      if (minZoom != null) source.minzoom = minZoom
      if (maxZoom != null) source.maxzoom = maxZoom

      safeAddSource(map, id, source)
    }

    if (url) {
      safeAddSource(map, id, {
        type: 'image',
        url,
        coordinates
      })
    }

    setSourceLoaded(true)

    return () => {
      safeRemoveSource(map, id)
    }
  }, [map])

  useEffect(() => {
    if (!hasLiveStyle(map)) return
    if (!sourceLoaded) return

    if (url && coordinates) {
      const source = map.getSource(id) as ImageSource
      if (source) source.updateImage({ url, coordinates })
    }
  }, [url, coordinates])

  if (!sourceLoaded) return null

  return <>{props.children}</>
}

export { MapRasterSource }
